'use client';

import React, { useMemo } from 'react';
import { cx } from '../utils/cx';
import type { ResolvedIcon } from '../../packages/icons/icon-registry';
import { IconRegistry } from '../../packages/icons/icon-registry';

export type FabVariant = 'primary' | 'secondary' | 'surface';
export type FabSize = 'sm' | 'md' | 'lg';
export type FabPosition = 'bottom-right' | 'bottom-left' | 'bottom-center';

export interface FabProps {
  icon: string;
  label?: string;
  ariaLabel?: string;
  extended?: boolean;
  variant?: FabVariant;
  size?: FabSize;
  position?: FabPosition;
  disabled?: boolean;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function sizeClass(size: FabSize): string {
  switch (size) {
    case 'sm':
      return 'h-10 px-4 text-sm';
    case 'lg':
      return 'h-14 px-6 text-base';
    case 'md':
    default:
      return 'h-12 px-5 text-sm';
  }
}

function iconSize(size: FabSize): number {
  switch (size) {
    case 'sm':
      return 18;
    case 'lg':
      return 22;
    case 'md':
    default:
      return 20;
  }
}

function variantClass(variant: FabVariant): string {
  switch (variant) {
    case 'secondary':
      return 'bg-secondary text-secondary ring-1 ring-secondary_alt hover:bg-secondary_hover';
    case 'surface':
      return 'bg-primary text-secondary ring-1 ring-secondary_alt hover:bg-primary_hover';
    case 'primary':
    default:
      return 'bg-brand text-on-brand ring-1 ring-brand hover:opacity-95';
  }
}

function positionClass(position: FabPosition): string {
  switch (position) {
    case 'bottom-left':
      return 'fixed bottom-6 left-6';
    case 'bottom-center':
      return 'fixed bottom-6 left-1/2 -translate-x-1/2';
    case 'bottom-right':
    default:
      return 'fixed bottom-6 right-6';
  }
}

export function Fab({
  icon,
  label,
  ariaLabel,
  extended = false,
  variant = 'primary',
  size = 'md',
  position = 'bottom-right',
  disabled = false,
  className,
  onClick,
}: FabProps) {
  const IconComp: ResolvedIcon | null = useMemo(() => IconRegistry.get(String(icon)), [icon]);
  const a11yLabel = String(ariaLabel ?? label ?? '').trim();

  if (!a11yLabel && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[Fab] Provide `label` or `ariaLabel` for accessibility.');
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={a11yLabel || undefined}
      onClick={onClick}
      className={cx(
        positionClass(position),
        'z-50 inline-flex items-center justify-center gap-2',
        'rounded-full shadow-lg',
        'transition active:scale-[0.98]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
        'disabled:cursor-not-allowed disabled:opacity-60',
        extended ? '' : 'w-12 px-0',
        sizeClass(size),
        variantClass(variant),
        className,
      )}
    >
      {IconComp ? <IconComp size={iconSize(size)} strokeWidth={2} aria-hidden="true" className="shrink-0" /> : null}
      {extended ? <span className="font-semibold">{label}</span> : null}
    </button>
  );
}

Fab.displayName = 'Fab';
